import { errorMessage } from '../i18n/errorMessages';
import type { MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';
import { useEffect, useId, useRef, useState, type FormEvent } from 'react';
import { Modal } from 'antd';
import { ApiError, type ChannelMember } from '../lib/api';
import { useChannelStore } from '../stores/useChannelStore';
import type { Channel } from '../types';
import { UserName } from './UserName';
import styles from './CreateChannelDialog.module.css';

interface Props {
  guildId: string;
  initialParentId: string | null;
  members: ChannelMember[];
  currentUserId: string;
  onClose: () => void;
  onCreated: (channel: Channel) => void;
}

export const CreateChannelDialog = ({
  guildId,
  initialParentId,
  members,
  currentUserId,
  onClose,
  onCreated,
}: Props) => {
  const { t, i18n } = useLingui();
  const zones = useChannelStore((state) => state.zones);
  const createChannel = useChannelStore((state) => state.createChannel);
  const loadChannels = useChannelStore((state) => state.loadChannels);
  const [name, setName] = useState('');
  const [topic, setTopic] = useState('');
  const [parentId, setParentId] = useState<string | null>(initialParentId);
  const [visibility, setVisibility] = useState<'public' | 'private'>('public');
  const [memberIds, setMemberIds] = useState<Set<string>>(new Set());
  const [creating, setCreating] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<MessageDescriptor | null>(null);
  const [rejectedParentId, setRejectedParentId] = useState<string | null>(null);
  const nameRef = useRef<HTMLInputElement>(null);
  const mounted = useRef(true);
  const formId = useId();
  const parentMissing = parentId !== null && !zones.some((zone) => zone.id === parentId);
  const invalidPlacement = parentId !== null && (parentMissing || parentId === rejectedParentId);
  const placementError = invalidPlacement
    ? t`The selected Zone is no longer available. Refresh Zones and explicitly choose a location.`
    : null;
  const sortedZones = [...zones].sort(
    (left, right) =>
      left.name.localeCompare(right.name, undefined, { sensitivity: 'base', numeric: true }) ||
      left.id.localeCompare(right.id),
  );

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!name.trim() || creating || invalidPlacement || refreshing) return;
    setCreating(true);
    setError(null);
    try {
      const channel = await createChannel(
        name.trim(),
        parentId,
        topic.trim() || undefined,
        visibility === 'private' && memberIds.size > 0 ? [...memberIds] : undefined,
        visibility,
      );
      if (mounted.current) onCreated(channel);
    } catch (cause) {
      if (cause instanceof ApiError && cause.code === 'zone.not_found' && parentId !== null)
        setRejectedParentId(parentId);
      setError(errorMessage(cause, msg`Failed to create channel`));
    } finally {
      setCreating(false);
    }
  };

  const refresh = async () => {
    setRefreshing(true);
    try {
      await loadChannels(guildId);
    } catch (cause) {
      setError(errorMessage(cause, msg`Failed to refresh Zones`));
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <Modal
      open
      title={t`Create channel`}
      width={480}
      className={styles.dialog}
      onCancel={onClose}
      closable={!creating}
      keyboard={!creating}
      mask={{ closable: !creating }}
      afterOpenChange={(open) => {
        if (open) nameRef.current?.focus();
      }}
      footer={null}
    >
      <form id={formId} className="channel-create-form" onSubmit={(event) => void submit(event)}>
        <div className={styles.body}>
          <p className={styles.description}>
            <Trans>
              Choose Default or a named Zone. The channel’s visibility and membership stay
              independent.
            </Trans>
          </p>
          <label className={styles.field}>
            <Trans>Channel name</Trans>
            <input
              ref={nameRef}
              className="input-field"
              placeholder={t`Channel name`}
              value={name}
              onChange={(event) => setName(event.target.value)}
              required
              autoComplete="off"
              disabled={creating}
            />
          </label>
          <label className={styles.field}>
            <Trans>Description (optional)</Trans>
            <input
              className="input-field"
              placeholder={t`Description (optional)`}
              value={topic}
              onChange={(event) => setTopic(event.target.value)}
              disabled={creating}
            />
          </label>
          <label className={styles.field}>
            <Trans>Zone</Trans>
            <select
              aria-label={t`Zone`}
              value={parentId === null ? 'default' : `zone:${parentId}`}
              aria-invalid={invalidPlacement}
              onChange={(event) => {
                setParentId(event.target.value === 'default' ? null : event.target.value.slice(5));
                setRejectedParentId(null);
                setError(null);
              }}
              disabled={creating || refreshing}
            >
              <option value="default">
                <Trans>Default (no Zone)</Trans>
              </option>
              {invalidPlacement && (
                <option value={`zone:${parentId}`} disabled>
                  <Trans>Unavailable Zone — choose a location</Trans>
                </option>
              )}
              {sortedZones
                .filter((zone) => !(invalidPlacement && zone.id === parentId))
                .map((zone) => (
                  <option key={zone.id} value={`zone:${zone.id}`}>
                    <Trans>▦ Zone: {zone.name}</Trans>
                  </option>
                ))}
            </select>
            <span className={styles.note}>
              <Trans>
                Default is the ungrouped location. Placement cannot be changed after creation.
              </Trans>
            </span>
          </label>
          <fieldset className={styles.visibility} disabled={creating}>
            <legend>
              <Trans>Visibility</Trans>
            </legend>
            <label className={styles.radioCard}>
              <input
                type="radio"
                name={`${formId}-visibility`}
                aria-label={t`Public`}
                checked={visibility === 'public'}
                onChange={() => setVisibility('public')}
              />
              <span>
                <strong>
                  <Trans>Public</Trans>
                </strong>
                <small>
                  <Trans>Project members can preview and join.</Trans>
                </small>
              </span>
            </label>
            <label className={styles.radioCard}>
              <input
                type="radio"
                name={`${formId}-visibility`}
                aria-label={t`Private`}
                checked={visibility === 'private'}
                onChange={() => setVisibility('private')}
              />
              <span>
                <strong>
                  <Trans>Private</Trans>
                </strong>
                <small>
                  <Trans>Only invited members can see this channel.</Trans>
                </small>
              </span>
            </label>
          </fieldset>
          {visibility === 'private' && (
            <fieldset className={styles.members} disabled={creating}>
              <legend>
                <Trans>Select members (optional)</Trans>
              </legend>
              {members
                .filter((member) => member.user_id !== currentUserId)
                .map((member) => (
                  <label key={member.user_id}>
                    <input
                      type="checkbox"
                      checked={memberIds.has(member.user_id)}
                      onChange={() =>
                        setMemberIds((previous) => {
                          const next = new Set(previous);
                          if (next.has(member.user_id)) next.delete(member.user_id);
                          else next.add(member.user_id);
                          return next;
                        })
                      }
                    />
                    <UserName displayName={member.display_name} role={member.role} />
                  </label>
                ))}
            </fieldset>
          )}
          {(placementError || error) && (
            <div className={styles.error} role="alert">
              {placementError && <div>{placementError}</div>}
              {error && <div>{i18n._(error)}</div>}
              <div>
                <button
                  type="button"
                  className="btn btn-sm"
                  onClick={() => void refresh()}
                  disabled={creating || refreshing}
                >
                  {refreshing ? t`Refreshing…` : t`Refresh Zones`}
                </button>
              </div>
            </div>
          )}
        </div>
        <div className={styles.footer}>
          <button type="button" className="btn btn-sm" onClick={onClose} disabled={creating}>
            <Trans>Cancel</Trans>
          </button>
          <button
            type="submit"
            className="btn btn-primary btn-sm"
            disabled={creating || refreshing || !name.trim() || invalidPlacement}
          >
            {creating ? t`Creating…` : t`Create channel`}
          </button>
        </div>
      </form>
    </Modal>
  );
};
